/**
 * Outbound Link Tracking Utility
 * Wraps external URLs with tracking endpoint
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate a tracked outbound URL
 * @param {string} url - The external URL to track
 * @param {string} source - Source identifier (e.g., 'adoption_agencies', 'public_feed')
 * @param {string} campaign - Optional campaign identifier
 * @returns {string} The tracking URL
 */
export const getTrackedUrl = (url, source = null, campaign = null) => {
  if (!url) return '#';
  
  // Don't track internal links
  try {
    const parsed = new URL(url);
    const currentHost = window.location.hostname;
    if (parsed.hostname === currentHost || parsed.hostname === 'localhost') {
      return url;
    }
  } catch {
    // If URL parsing fails, return original
    return url;
  }
  
  const params = new URLSearchParams();
  params.set('url', url);
  if (source) params.set('source', source);
  if (campaign) params.set('campaign', campaign);
  
  return `${API_URL}/api/outbound?${params.toString()}`;
};

/**
 * Check if a URL is external
 * @param {string} url - URL to check
 * @returns {boolean}
 */
export const isExternalUrl = (url) => {
  if (!url) return false;
  
  try {
    const parsed = new URL(url);
    const currentHost = window.location.hostname;
    return parsed.hostname !== currentHost && parsed.hostname !== 'localhost';
  } catch {
    return false;
  }
};

/**
 * TrackedLink component props helper
 * Use with anchor tags: <a {...getTrackedLinkProps(url, source)} />
 */
export const getTrackedLinkProps = (url, source = null, campaign = null) => {
  const isExternal = isExternalUrl(url);
  
  return {
    href: isExternal ? getTrackedUrl(url, source, campaign) : url,
    target: isExternal ? '_blank' : undefined,
    rel: isExternal ? 'noopener noreferrer' : undefined
  };
};

export default {
  getTrackedUrl,
  isExternalUrl,
  getTrackedLinkProps
};
